import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Marker, Popup, Tooltip, useMap } from 'react-leaflet'
import L from 'leaflet'

import { ensurePointVisible, ENTITY_POINT_ZOOM } from '../mapCamera'
import { createClusterIcon } from './mapMarkerIcons'
import {
  isClusterCoincident,
  zoomToCluster,
  type ClusterZoomViewport,
  type MarkerCluster,
} from './markerClustering'

function ClusterMapMarkerInner({
  cluster,
  viewport,
  onSelectMember,
}: {
  cluster: MarkerCluster
  viewport: ClusterZoomViewport
  onSelectMember: (markerKey: string) => void
}) {
  const map = useMap()
  const markerRef = useRef<L.Marker | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const coincident = useMemo(() => isClusterCoincident(cluster), [cluster])
  const icon = useMemo(() => createClusterIcon(cluster.count), [cluster.count])

  useEffect(() => {
    setMenuOpen(false)
  }, [cluster.id])

  useEffect(() => {
    const marker = markerRef.current
    if (!marker) return
    if (menuOpen) marker.openPopup()
    else marker.closePopup()
  }, [menuOpen])

  const onClick = useCallback(
    (e: L.LeafletMouseEvent) => {
      L.DomEvent.stop(e)
      if (coincident) {
        ensurePointVisible(map, cluster.lat, cluster.lng, {
          preferredZoom: Math.max(map.getZoom(), ENTITY_POINT_ZOOM),
          forceCenter: true,
          focusAnchor:
            viewport.outputOpen && viewport.isMobile
              ? { x: 0.5, y: 0.25 }
              : { x: 0.5, y: 0.5 },
        })
        setMenuOpen(true)
        return
      }
      setMenuOpen(false)
      zoomToCluster(map, cluster, viewport)
    },
    [map, cluster, viewport, coincident],
  )

  const eventHandlers = useMemo(() => ({ click: onClick }), [onClick])

  const pickMember = useCallback(
    (key: string) => {
      setMenuOpen(false)
      onSelectMember(key)
    },
    [onSelectMember],
  )

  return (
    <Marker
      ref={markerRef}
      position={[cluster.lat, cluster.lng]}
      icon={icon}
      zIndexOffset={menuOpen ? 700 : 500}
      eventHandlers={eventHandlers}
    >
      <Tooltip direction="top" offset={[0, -16]} opacity={0.96}>
        <div className="text-xs font-semibold leading-tight">
          {coincident
            ? `${cluster.count} at this location`
            : `${cluster.count} locations`}
        </div>
      </Tooltip>
      {coincident ? (
        <Popup
          className="control-center-cluster-member-popup"
          offset={[0, 18]}
          autoPan={false}
          closeOnClick
          autoClose
          closeButton={false}
          eventHandlers={{
            remove: () => setMenuOpen(false),
          }}
        >
          <div
            className="min-w-[13rem] max-w-[18rem]"
            role="menu"
            aria-label="Choose a location"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-0.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
              {cluster.count} at this location
            </div>
            <div className="max-h-56 overflow-y-auto">
              {cluster.markers.map((member) => (
                <button
                  key={member.key}
                  type="button"
                  role="menuitem"
                  className="flex w-full items-start gap-2 rounded-lg px-1.5 py-1.5 text-left transition-colors hover:bg-base-200"
                  onClick={() => pickMember(member.key)}
                >
                  <span
                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: member.color }}
                    aria-hidden
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold leading-tight text-base-content">
                      {member.title}
                    </span>
                    {member.subtitle ? (
                      <span className="mt-0.5 block text-[10px] leading-snug text-base-content/60">
                        {member.subtitle}
                      </span>
                    ) : null}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Popup>
      ) : null}
    </Marker>
  )
}

export const ClusterMapMarker = memo(ClusterMapMarkerInner)
